import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, User as UserIcon, KeyRound } from 'lucide-react';
import AdminLayout from '../../components/Layout/AdminLayout';
import Toast, { useToasts } from '../../components/Layout/Toast';
import { monitorApi } from '../../services/api';
import { formatDate } from '../../config/appConfig';
import { User } from '../../types';

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const toast = useToasts();
  const currentUser = window.localStorage.getItem('auth_user');

  const load = useCallback(async () => {
    try { const res = await monitorApi.getUsers(); setUsers(res.data); }
    catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await monitorApi.createUser({ username, password });
      setUsername(''); setPassword('');
      toast.success('User created');
      load();
    } catch (err: any) { toast.error(err.response?.data?.error || 'Failed to create user'); }
  };

  const remove = async (u: User) => {
    if (!window.confirm(`Delete user "${u.username}"?`)) return;
    try { await monitorApi.deleteUser(u.id); toast.success('User deleted'); load(); }
    catch (err: any) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const resetPassword = async (u: User) => {
    const pw = window.prompt(`Enter a new password for "${u.username}":`);
    if (!pw) return;
    try { await monitorApi.changePassword(u.id, pw); toast.success('Password updated'); }
    catch { toast.error('Failed to update password'); }
  };

  const input = 'w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40';

  return (
    <AdminLayout>
      <Toast toasts={toast.toasts} onDismiss={toast.dismiss} />
      <div className="max-w-4xl mx-auto p-8">
        <h1 className="text-2xl font-bold text-slate-800 mb-1">Admin Users</h1>
        <p className="text-sm text-slate-500 mb-6">Admin accounts can create and configure monitors.</p>

        <form onSubmit={add} className="bg-white rounded-xl border border-slate-200 p-5 mb-6 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Username</label>
            <input className={input} value={username} onChange={e => setUsername(e.target.value)} required autoComplete="off" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Password</label>
            <input type="password" className={input} value={password} onChange={e => setPassword(e.target.value)} required autoComplete="new-password" />
          </div>
          <button type="submit" className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-brand text-white rounded-md hover:bg-brand-dark h-[38px]">
            <Plus size={16} /> Add
          </button>
        </form>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="px-5 py-16 text-center text-slate-400">Loading…</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center"><UserIcon size={18} /></div>
                    <div>
                      <div className="font-medium text-slate-800">{u.username} {u.username === currentUser && <span className="text-xs text-slate-400">(you)</span>}</div>
                      <div className="text-xs text-slate-400">{u.role} · created {formatDate(u.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => resetPassword(u)} title="Reset password" className="p-1.5 text-slate-500 hover:text-brand hover:bg-slate-100 rounded"><KeyRound size={16} /></button>
                    <button onClick={() => remove(u)} title="Delete" className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-slate-100 rounded"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsersPage;
